require('./brokerStocksLikeHeader.scss');

const PropTypes = require('prop-types');

// @vendors
const React = require('react');
const moment = require('moment');
const classnames = require('classnames');
const { connect } = require('react-redux');
const { withRouter, Prompt } = require('react-router-dom');

// @commons
const { EmptyState } = require('commonsComponents/emptyState');
const { ContentBox } = require('commonsComponents/contentBox');
const { OKLink } = require('commonsComponents/OKLink');
const { OKButton } = require('components/commons/OKButton/OKButton');
const StockExchangeIndexVariation = require('components/broker/commons/stockExchange/stockExchangeIndexVariation.js');
const Flag = require('components/broker/commons/flag/flag');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const { OKMessage } = require('commonsComponents/OKMessage');

// @components
const { LoadingSection } = require('commonsComponents/loadingSection');
const { OneLineSymbolAmount } = require('commonsComponents/oneLineSymbolAmount');
const BrokerMiFIDUSPersonButton = require('mifid/components/brokerMiFIDUSPersonButton/brokerMiFIDUSPersonButton');
const { BrokerClientWarningModal } = require('components/broker/commons/brokerClientWarningModal/brokerClientWarningModal');
const BrokerProductUserInfo = require('components/broker/brokerProductUserInfo/brokerProductUserInfo');
const { BrokerStockInformativeButton } = require('components/broker/brokerStockInformativeButton/brokerStockInformativeButton');
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');
const BrokerRiskIndicator = require('components/broker/brokerRiskIndicator/brokerRiskIndicator');

// @actions
const BrokerClientWarningModalAction = require('actions/brokerClientWarningModal');
const BrokerMyInvestmentsActions = require('actions/brokerMyInvestments');

// @helpers
const { formatAmount, formatAmountObject, getPlainAmountObject } = require('utilities/currencyHelper');
const { toFirstUppercase } = require('utilities/stringHelper');
const { formatText, FormattedText, getLanguage } = require('core/i18n').i18n;
const sanitizeHelper = require('utilities/sanitizeHelper');
const headerHelper = require('components/broker/brokerProductHeaders/brokerProductHeaderHelper');

// @constants
const MICCODE_CONVERSION = require('constants/miccodes');
const PREFIX = 'broker-stocksLikeHeader';
const EMPTY_DATA_GO_BACK_LINK = '/myprofile/broker/explore';
const {
    MYPROFILE_BROKER_ERROR_URL,
    BROKER_CORPORATE_OPERATIONS_URL,
    BROKER_TYPE_STOCKS_NEW_DECIMALS,
    COLON,
    DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR
} = require('constants/index');
const { NOT_ALLOWED_WEALTH_PRODUCT_NAME_FROM_CATALOGUE_COUNTRIES } = require('constants/flagBasedFeatures');
const language = getLanguage();

// @ constants
const { BROKER_LARGE_FLAG_SIZE } = require('constants/index');

class BrokerProductFileHeader extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            nextLocation: null,
            isBlockingRouteChange: false,
        };

        this.buildBuyUrl = this.buildBuyUrl.bind(this);
        this.createBottomButtons = this.createBottomButtons.bind(this);
        this.handleGoToBuyProduct = this.handleGoToBuyProduct.bind(this);
        this.handleGoToSellProduct = this.handleGoToSellProduct.bind(this);
        this.buildLastPriceSection = this.buildLastPriceSection.bind(this);
        this.buildAssociatedDerechos = this.buildAssociatedDerechos.bind(this);
    }

    componentDidMount() {
        const { immMyInvestments, fetchMyInvestments } = this.props;
        if (!immMyInvestments.get('fetchSuccess') && !immMyInvestments.get('isFetching') && !immMyInvestments.get('errorReceived')) {
            fetchMyInvestments();
        }
    }

    buildBrokerProductUserInfo(possessions) {
        const { noBottomButtons, immMyInvestments } = this.props;
        if (immMyInvestments.get('fetchSuccess')) {
            return (
                <BrokerProductUserInfo
                    noBottomButtons={noBottomButtons}
                    immStock={possessions}
                />
            );
        }
    }

    buildBuyUrl() {
        const { modifier, isin, iso } = this.props;
        return `/myprofile/broker/${modifier}Buy/${isin}/${iso}`;
    }

    buildErrorSection() {
        const { productFile } = this.props;
        const error = productFile.get('error') || {};
        const isEmptyResponse = error.error === null && error.errorDescription === null;

        let primaryText;
        let secondaryText;
        let button;

        if (isEmptyResponse) {
            primaryText = formatText(`${PREFIX}NoInformationForProduct`);
            secondaryText = formatText(`${PREFIX}ContactUsToResolveIssue`);
            button = (
                <OKLink modifier="emphasize">
                    <FormattedText
                        value={`${PREFIX}GoBack`}
                        to={EMPTY_DATA_GO_BACK_LINK}
                    />
                </OKLink>
            );
        } else {
            primaryText = formatText(`${PREFIX}LostConnectionFirstLine`);
            button = (
                <OKButton onClick={this.props.handleUpdate}>
                    <FormattedText value={`${PREFIX}Retry`} />
                </OKButton>
            );
        }

        return (
            <EmptyState primaryText={primaryText} secondaryText={secondaryText} button={button} iconType="nointernet-wealth" />
        );
    }

    buildHeader() {
        const { productFile, modifier, etfsProduct, immFlagBasedFeatures } = this.props;
        const isinCode = productFile.get('isinCode');
        const isoCode = productFile.get('micCode');
        let riskScale = productFile.get('risk');

        const variation = productFile.getIn(['indexValue', 'variation']);
        const productValueObject = productFile.get('productValue').set('decimalsLength', BROKER_TYPE_STOCKS_NEW_DECIMALS);
        const percentage = productFile.getIn(['indexValue', 'percentage']);

        const plainAmountObject = getPlainAmountObject(productValueObject);
        const quoteModifier = toFirstUppercase(modifier);
        const isinCodeLabel = formatText(`${PREFIX}IsinCodeLabel`);
        const quoteLabel = formatText(`${PREFIX}Quote${quoteModifier}Label`);
        const ticker = modifier === 'stocks' ? '(' + productFile.get('marketCode') + ')' : '';
        let renderEtfRisk = false;
        let marketCode, flag;
        let companyName;
        const allowCatalogueNames = immFlagBasedFeatures && immFlagBasedFeatures.get(NOT_ALLOWED_WEALTH_PRODUCT_NAME_FROM_CATALOGUE_COUNTRIES);

        if (allowCatalogueNames) {
            const nameOrigin = modifier === 'stocks' ? productFile : etfsProduct
            companyName = nameOrigin.get('stockName');
        } else {
            companyName = productFile.get('companyName');
        }


        if (modifier === 'stocks' || modifier === 'etfs') {
            const market = MICCODE_CONVERSION.find(market => market.MIC == isoCode);
            marketCode = market ? ` ${market.CITY}, ${market.ISO_COUNTRY_CODE}` : '';

            const countryCode = productFile.getIn(['country', 'id']);
            flag = <Flag countryCode={countryCode} pixelsSize={BROKER_LARGE_FLAG_SIZE} />
        }

        if (modifier === 'etfs' && etfsProduct) {
            renderEtfRisk = true;
            const otherRisk = etfsProduct.get('riskRange') ? etfsProduct.get('riskRange').substr(0, 1) : 0;
            riskScale = etfsProduct.get('riskScale') > 0 ? etfsProduct.get('riskScale') : parseInt(otherRisk);
        }

        return (
            <div className="broker-stocks-like-header__header row">
                <div className="col-lg-12">
                    <div className="broker-stocks-like-header__company-header">
                        <h2 className="broker-stocks-like-header__company-name">
                            {sanitizeHelper.sanitizeData(companyName)} {ticker}
                        </h2>
                    </div>
                    <span className="broker-stocks-like-header__isin">
                        {isinCodeLabel}: {isinCode}
                        {' - '}
                        {flag}
                        {marketCode}
                    </span>
                </div>
                {
                    renderEtfRisk && (
                        <div className="col-lg-12">
                            <div className="broker-stocks-like-header__risk-indicator">
                                <BrokerRiskIndicator
                                    riskValue={riskScale}
                                    textContent="broker-stocksLikeHeaderRiskContent"
                                    productType="Stocks"
                                />
                            </div>
                        </div>
                    )
                }
                <div className="col-lg-12">
                    <div className="broker-stocks-like-header__quote-box">
                        <span className="broker-stocks-like-header__quote">
                            {quoteLabel}
                        </span>
                        <div className="broker-stocks-like-header__product-quote-container">
                            <div className="broker-stocks-like-header__product-value-container">
                                <OneLineSymbolAmount
                                    amountObject={plainAmountObject}
                                    className="broker-stocks-like-header__product-value"
                                    language={language}
                                    amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                                />
                                <i className="broker-stocks-like-header__icon-notification icon icon-notificaciones" />
                            </div>
                            <div className="broker-stocks-like-header__index-box">
                                <span className="broker-stocks-like-header__index-value">
                                    <StockExchangeIndexVariation
                                        variationPts={formatAmount(variation, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)}
                                        variationPerc={formatAmount(percentage)}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    buildLastPriceSection() {
        const { productFile } = this.props;

        const lastPriceTime = productFile.get('lastPriceTime');
        const lastPriceDate = productFile.get('lastPriceDate');

        const possessionsSection = this.buildPossessions();


        const dateFormat = formatText(`${PREFIX}DateFormat`);
        let formattedLastPriceDate = '';
        let lastPriceLabelValue = '';
        const hasPossessions = possessionsSection ?
            'broker-stocks-like-header__last-price-container' : 'broker-stocks-like-header__last-price-container-not-possesions'

        if (lastPriceDate) {
            formattedLastPriceDate = moment(lastPriceDate, DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR)
                .format(dateFormat)
                .replace('.', '');
        }

        if (!!lastPriceDate && !!lastPriceTime) {
            lastPriceLabelValue = (
                <FormattedTextWithComponents
                    value={`${PREFIX}QuotationLabel`}
                    injectedComponents={[lastPriceTime, formattedLastPriceDate]}
                    className="broker-stocks-like-header__last-price-date"
                />)
        }

        return (
            <div className="broker-stocks-like-header__wallet-last-price-section row">
                {possessionsSection && (
                    <div className="col-lg-6">
                        {possessionsSection}
                    </div>
                )}
                <div className="col-lg-6">
                    <div className={hasPossessions}>
                        {lastPriceLabelValue}
                    </div>
                </div>
            </div>
        );
    }

    buildLoadingSection() {
        return <LoadingSection />;
    }

    buildMarketsSection() {
        const noButtons = this.props.noBottomButtons;
        const { productFile } = this.props;
        const buyAmount = productFile.get('buyAmount');
        const buyPrice = productFile.get('buyPrice');
        const sellPrice = productFile.get('sellPrice');
        const sellAmount = productFile.get('sellAmount');
        const minimum = productFile.get('minimum');
        const maximum = productFile.get('maximum');
        const opening = productFile.get('opening');
        const lastClose = productFile.get('lastClose');
        const volume = productFile.get('volume');

        const buyVolumeLabel = formatText(`${PREFIX}BuyVolumeLabel`).split(' ');
        const buyPriceLabel = formatText(`${PREFIX}BuyPriceLabel`);
        const sellPriceLabel = formatText(`${PREFIX}SellPriceLabel`);
        const sellVolumeLabel = formatText(`${PREFIX}SellVolumeLabel`).split(' ');
        const minimumLabel = formatText(`${PREFIX}MinimumLabel`);
        const maximumLabel = formatText(`${PREFIX}MaximumLabel`);
        const openingLabel = formatText(`${PREFIX}OpeningLabel`);
        const lastCloseLabel = formatText(`${PREFIX}LastCloseLabel`);
        const volumeLabel = formatText(`${PREFIX}VolumeLabel`);

        const marketValuesClassNames = classnames('broker-stocks-like-header__markets-values', {
            'broker-stocks-like-header__buttonless': noButtons
        });

        const currencySymbol = headerHelper.getCurrencySimbol(productFile.get('currency'));

        const volumeBuyAmountLabel = headerHelper.getTitleLabel(productFile.get('buyAmount'));
        const volumeSellAmountlabel = headerHelper.getTitleLabel(productFile.get('sellAmount'));
        const volumeTotalLabel = headerHelper.getTitleLabel(productFile.get('volume'));

        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className={marketValuesClassNames}>
                        <div className="col-xs-6 col-lg-3 broker-stocks-like-header__market-buy-info">
                            <div className="col-xs-6 col-lg-6 broker-stocks-like-header__market-buy-box">
                                <div className="broker-stocks-like-header__market-info-wrapper">
                                    <span className="broker-stocks-like-header__market-info">
                                        {buyVolumeLabel[0]}
                                        <br />
                                        {buyVolumeLabel[1]}
                                    </span>
                                    <OKTooltip
                                        content={`${PREFIX}BuyVolumeLabelTooltip`}
                                        offset={[0, -15]}
                                    />
                                </div>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(buyAmount, undefined, 0)} {volumeBuyAmountLabel}
                                </span>
                            </div>

                            <div className="col-xs-6 col-lg-6 broker-stocks-like-header__market-buy-price-info">
                                <div className="broker-stocks-like-header__market-info-wrapper">
                                    <span className="broker-stocks-like-header__market-info">
                                        {buyPriceLabel}
                                    </span>
                                    <OKTooltip
                                        content={`${PREFIX}BuyPriceLabelTooltip`}
                                        offset={[0, -15]}
                                    />
                                </div>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(buyPrice, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>
                        </div>

                        <div className="col-xs-6 col-lg-3 broker-stocks-like-header__market-sell-info">
                            <div className="col-xs-6 col-lg-6">
                                <div className="broker-stocks-like-header__market-info-wrapper">
                                    <span className="broker-stocks-like-header__market-info">
                                        {sellPriceLabel}
                                    </span>
                                    <OKTooltip
                                        content={`${PREFIX}SellPriceLabelTooltip`}
                                        offset={[0, -15]}
                                    />
                                </div>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(sellPrice, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>

                            <div className="col-xs-6 col-lg-6 broker-stocks-like-header__market-sell-volume-info">
                                <div className="broker-stocks-like-header__market-info-wrapper">
                                    <span className="broker-stocks-like-header__market-info">
                                        {sellVolumeLabel[0]}
                                        <br />
                                        {sellVolumeLabel[1]}
                                    </span>
                                    <OKTooltip
                                        content={`${PREFIX}SellVolumeLabelTooltip`}
                                        offset={[0, -15]}
                                    />
                                </div>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(sellAmount, undefined, 0)} {volumeSellAmountlabel}
                                </span>
                            </div>
                        </div>

                        <div className="col-xs-12 col-lg-5 broker-stocks-like-header__market-close-info">
                            <div className="col-xs-3 col-lg-3 broker-stocks-like-header__market-maximum-info">
                                <span className="broker-stocks-like-header__market-info">
                                    {maximumLabel}
                                </span>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(maximum, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>

                            <div className="col-xs-3 col-lg-3 broker-stocks-like-header__market-minimum-info">
                                <span className="broker-stocks-like-header__market-info">
                                    {minimumLabel}
                                </span>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(minimum, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>

                            <div className="col-xs-3 col-lg-3 broker-stocks-like-header__market-minimum-opening">
                                <span className="broker-stocks-like-header__market-info">
                                    {openingLabel}
                                </span>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(opening, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>

                            <div className="col-xs-3 col-lg-3 broker-stocks-like-header__market-last-close-box">
                                <span className="broker-stocks-like-header__market-info">
                                    {lastCloseLabel}
                                </span>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(lastClose, undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)} {currencySymbol}
                                </span>
                            </div>
                        </div>

                        <div className="col-xs-12 col-lg-1 broker-stocks-like-header__market-volume-info">
                            <div>
                                <span className="broker-stocks-like-header__market-info">
                                    {volumeLabel}
                                </span>
                                <span className="broker-stocks-like-header__market-value">
                                    {formatAmount(volume, undefined, 0)} {volumeTotalLabel}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    getTitlesQuantity() {
        const { immMyInvestments, productFile, modifier } = this.props;
        const immStocks = immMyInvestments.getIn(['products', modifier, 'stocks'], []);
        let titlesQuantity = 0;
        let titlesValuation = 0.0;
        let possessionsCurrency;

        immStocks.forEach(immStock => {
            if (immStock.get('isin') === productFile.get('isinCode') && immStock.get('iso') === productFile.get('micCode')) {
                possessionsCurrency = possessionsCurrency ? possessionsCurrency : immStock.get('totalValueCurrency');
                titlesQuantity += immStock.get('shares', 0);
                titlesValuation += immStock.get('totalValue', 0.0);
                if (immStock.getIn(['unconsolidatedOperations', 'hasUnconsolidatedOperations'])) {
                    titlesQuantity += immStock.getIn(['unconsolidatedOperations', 'shares'], 0);
                    titlesValuation += immStock.getIn(['unconsolidatedOperations', 'totalValue'], 0.0);
                }
            }
        });

        return {
            titlesQuantity,
            titlesValuation,
            possessionsCurrency
        }
    }

    buildPossessions() {
        const {
            titlesQuantity,
            titlesValuation,
            possessionsCurrency
        } = this.getTitlesQuantity();

        const walletLabel = formatText(`${PREFIX}WalletLabel`);
        const titlesLabel = headerHelper.getTitleLabel(titlesQuantity);

        const valuationObject = {
            amount: titlesValuation,
            currency: possessionsCurrency
        };

        const plainAmountObject = getPlainAmountObject(valuationObject);
        const balanceSymbol = (
            <OneLineSymbolAmount
                amountObject={plainAmountObject}
                language={language}
                amountObjectFormatted={formatAmountObject(plainAmountObject, true)} />
        );

        if (!titlesQuantity) {
            return false;
        }

        const titlesQuant = formatAmount(titlesQuantity, undefined, 0);

        return (
            <span className="broker-stocks-like-header__wallet">
                {walletLabel}: {titlesQuant} {titlesLabel} | {balanceSymbol}
            </span>
        );
    }

    buildAssociatedDerechos() {
        const { immMyInvestments, modifier, productFile } = this.props;
        const stocks = immMyInvestments.getIn(['products', modifier, 'stocks'], []);
        const stock = stocks.find(stock => {
            return (
                stock.get('isin') === productFile.get('isinCode') &&
                stock.get('iso') === productFile.get('micCode')
            );
        });
        const associatedDerechos = stock && stock.get('derechosAssociado');

        if (!!associatedDerechos) {
            const nombre = associatedDerechos.get('nombre');
            const isin = associatedDerechos.get('isin');
            const importesTotal = associatedDerechos.get('importesTotal');
            const proportionRightsRatio = associatedDerechos.get('proportionRightsRatio');
            const proportionSharesRatio = associatedDerechos.get('proportionSharesRatio');
            const endDate = associatedDerechos.get('endDate');

            const infoTextLabel = `${formatText('broker-derechosCorrespondidos')} ${importesTotal}
                                    (${proportionRightsRatio} x ${proportionSharesRatio})`;

            const isinLabel = `${formatText(
                'broker-associatedDerechosISINLabel'
            )}${COLON} ${isin}`;

            const endDateLabel = `${formatText('broker-instructionDeadline')} ${endDate}`;

            return (
                <div className="broker-stocks-like-header__derechos">
                    <OKMessage type="informative">
                        <div className="col-xs-4 broker-stocks-like-header__derechos-info">
                            <span className="broker-stocks-like-header__derechos-name">{nombre}</span>
                            <span className="broker-stocks-like-header__derechos-correspondidos">{infoTextLabel}</span>
                        </div>
                        <div className="col-xs-6 broker-stocks-like-header__derechos-details">
                            <span className="broker-stocks-like-header__derechos-isin">{isinLabel}</span>
                            <span className="broker-stocks-like-header__derechos-enddate">{endDateLabel}</span>
                        </div>
                        <div className="col-xs-2 broker-stocks-like-header__derechos-button-container">
                            <OKButton
                                modifier="secondary"
                                to={BROKER_CORPORATE_OPERATIONS_URL}
                            >{formatText('broker-seeMore')}</OKButton>
                        </div>
                    </OKMessage>
                </div>
            );
        }
    }

    createBottomButtons() {
        const { immMyInvestments, modifier, productFile, isin, iso } = this.props;
        const noButtons = this.props.noBottomButtons;

        const stocks = immMyInvestments.getIn(['products', modifier, 'stocks'], []);
        const stock = stocks.find(stock => {
            return (
                stock.get('isin') === productFile.get('isinCode') &&
                stock.get('iso') === productFile.get('micCode')

            );
        });

        const titlesQuantity = stock ? stock.get('shares') : '';
        const crfRequired = productFile.get('crfRequired');
        const w8Required = productFile.get('w8Required');
        const buyLabel = formatText(`${PREFIX}BuyLabel`);
        const sellLabel = formatText(`${PREFIX}SellLabel`);
        let sellButton;

        if (!noButtons) {
            let buyButton = (
                <BrokerStockInformativeButton
                    productType={modifier}
                    crfRequired={crfRequired}
                    w8Required={w8Required}
                >
                    <BrokerMiFIDUSPersonButton
                        modifier="secondary"
                        onClick={this.handleGoToBuyProduct}
                        isinId={isin}
                        marketId={iso}
                    >
                        {buyLabel}
                    </BrokerMiFIDUSPersonButton>
                </BrokerStockInformativeButton>
            );

            if (titlesQuantity) {
                sellButton = (
                    <div className="broker-stocks-like-header__button-container">
                        <OKButton
                            modifier="ghost"
                            onClick={this.handleGoToSellProduct}
                        >
                            {sellLabel}
                        </OKButton>
                    </div>
                );
            }

            return (
                <div className="row">
                    <div className="col-lg-offset-8 col-lg-4">
                        <div className="broker-stocks-like-header__buttons">
                            {sellButton}
                            <div className="broker-stocks-like-header__button-container">
                                {buyButton}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    handleGoToBuyProduct() {
        const buyLink = this.buildBuyUrl();
        const { immbrokerClientWarningModal, showClientWarningModal, history } = this.props;
        const clientPersonalInfoUpdated = immbrokerClientWarningModal.get('clientPersonalInfoUpdated');
        const isFetching = immbrokerClientWarningModal.get('isFetching');
        const isFetched = immbrokerClientWarningModal.get('isFetched');
        this.props.fireTaggingBuyButton && this.props.fireTaggingBuyButton(this.props.isin)
        if (isFetched && !isFetching) {
            if (!clientPersonalInfoUpdated) {
                showClientWarningModal(history);
                this.setState({
                    nextLocation: buyLink,
                    isBlockingRouteChange: true
                });
            } else {
                history.push(buyLink);
            }
        } else if (!isFetched && !isFetching) {
            history.push(MYPROFILE_BROKER_ERROR_URL);
        }
        window.modalInstance = this;
    }

    handleGoToSellProduct() {
        const { modifier, immMyInvestments, isin, iso } = this.props;

        if (isin && iso) {
            const stocks = immMyInvestments.getIn(['products', modifier, 'stocks'], []);
            const possessions = stocks.find(stock => {
                return stock.get('isin') === isin && stock.get('iso') === iso;
            });

            const emisionCode = possessions ? possessions.get('emisionCode') : '';
            const valueCode = possessions ? possessions.get('valueCode') : '';
            const sellLink = `/myprofile/broker/${modifier}Sell/${isin}/${iso}/${emisionCode}/${valueCode}`;

            const { immbrokerClientWarningModal, showClientWarningModal, history } = this.props;
            const clientPersonalInfoUpdated = immbrokerClientWarningModal.get('clientPersonalInfoUpdated');
            const isFetching = immbrokerClientWarningModal.get('isFetching');
            const isFetched = immbrokerClientWarningModal.get('isFetched');

            if (isFetched && !isFetching) {
                if (!clientPersonalInfoUpdated) {
                    showClientWarningModal(history);
                    this.setState({
                        nextLocation: sellLink,
                        isBlockingRouteChange: true
                    });
                } else {
                    history.push(sellLink);
                }
            } else if (!isFetched && !isFetching) {
                history.push(MYPROFILE_BROKER_ERROR_URL);
            }
            window.modalInstance = this;
        }
    }

    render() {
        const { productFile, immMyInvestments, modifier, isin, iso, etfsProduct } = this.props;
        const customClass = classnames('broker-stocks-like-header', {
            ['broker-stocks-like-header--' + modifier]: !!modifier
        });

        let content;
        const isFetchingProductFile = productFile.get('isFetching');
        const isFetchedStocksCatalogue = productFile && productFile.get('isFetchedCatalogue');
        const isFetchedEtfsCatalogue = etfsProduct && etfsProduct.get('fetchedCatalogue');
        const isFetchedCurrentCatalogue = modifier === "stocks" ? isFetchedStocksCatalogue : isFetchedEtfsCatalogue
        if (productFile.get('error')) {
            content = this.buildErrorSection();
        } else if (isFetchingProductFile || !isFetchedCurrentCatalogue) {
            content = this.buildLoadingSection();
        } else {
            const stocks = immMyInvestments.getIn(['products', modifier, 'stocks'], []);
            const isinValue = isin ? isin : productFile.get('isinCode');
            const isoValue = iso ? iso : productFile.get('micCode');
            const possessions = stocks.find(stock => {
                return stock.get('isin') === isinValue && stock.get('iso') === isoValue;
            });
            const bottomButtons = this.createBottomButtons(possessions);

            content = [
                this.buildHeader(),
                this.buildLastPriceSection(),
                this.buildAssociatedDerechos(),
                this.buildMarketsSection(),
                bottomButtons
            ];
        }

        return (
            <React.Fragment>
                <Prompt message={() => false} when={this.state.isBlockingRouteChange} />
                <div>
                    <BrokerClientWarningModal
                        nextLocation={this.state.nextLocation}
                        routerHookCallback={() => {
                            this.setState({ isBlockingRouteChange: false });
                        }}
                    />
                    <ContentBox id="brokerStocksLikeHeader" customClassName={customClass}>
                        {content}
                    </ContentBox>
                </div>
            </React.Fragment>
        );
    }
}

BrokerProductFileHeader.propTypes = {
    modifier: PropTypes.oneOf(['stocks', 'etfs']).isRequired,
    productFile: PropTypes.object,
    immMyInvestments: PropTypes.object.isRequired,
    isin: PropTypes.string,
    iso: PropTypes.string,
    handleUpdate: PropTypes.func,
    immbrokerClientWarningModal: PropTypes.object,
    showClientWarningModal: PropTypes.func,
    noBottomButtons: PropTypes.bool,
    fetchMyInvestments: PropTypes.func,
    etfsProduct: PropTypes.object,
    fireTaggingBuyButton: PropTypes.func,
    history: PropTypes.object.isRequired,
};

module.exports.BrokerProductFileHeader = withRouter(connect(
    state => ({
        immbrokerClientWarningModal: state.brokerClientWarningModal,
        immMyInvestments: state.brokerMyInvestments,
    }),
    {
        showClientWarningModal: BrokerClientWarningModalAction.showClientWarningModal,
        fetchMyInvestments: BrokerMyInvestmentsActions.fetchMyInvestments
    }
)(BrokerProductFileHeader));
